import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  Clock,
  Eye,
  Mail,
  RotateCcw,
  Send,
  TrendingUp,
} from 'lucide-react'
import type { EmailStats } from './types'

export default function EmailOverviewTab({
  stats,
  loading,
}: {
  stats?: EmailStats & { hasData?: boolean }
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-lg bg-white p-6 shadow dark:bg-gray-800"
          >
            <div className="mb-2 h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div className="h-8 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="rounded-lg bg-white p-6 text-center shadow dark:bg-gray-800">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          통계 데이터를 불러올 수 없습니다
        </h3>
        <p className="mb-4 text-gray-500 dark:text-gray-400">
          서버 연결에 문제가 있습니다. 잠시 후 다시 시도해주세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          새로고침
        </button>
      </div>
    )
  }

  // 데이터가 없는 경우 안내
  if (stats.hasData === false) {
    return (
      <div className="rounded-lg bg-white p-6 text-center shadow dark:bg-gray-800">
        <Mail className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          아직 발송된 메일이 없습니다
        </h3>
        <p className="mb-4 text-gray-500 dark:text-gray-400">
          메일 발송 탭에서 첫 번째 메일을 발송해보세요.
        </p>
        <button
          onClick={() => (window.location.href = '#send')}
          className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Send className="mr-2 h-4 w-4" />
          메일 발송하기
        </button>
      </div>
    )
  }

  const overviewCards = [
    {
      title: '총 발송',
      value: (stats.overview?.total || 0).toLocaleString(),
      icon: Mail,
      color: 'blue',
      change: stats.recent24h?.total || 0,
    },
    {
      title: '성공적 발송',
      value: (stats.overview?.sent || 0).toLocaleString(),
      icon: CheckCircle,
      color: 'green',
      change: stats.recent24h?.sent || 0,
    },
    {
      title: '실패',
      value: (stats.overview?.failed || 0).toLocaleString(),
      icon: AlertCircle,
      color: 'red',
      change: stats.recent24h?.failed || 0,
    },
    {
      title: '대기 중',
      value: (stats.overview?.pending || 0).toLocaleString(),
      icon: Clock,
      color: 'yellow',
      change: stats.recent24h?.pending || 0,
    },
    {
      title: '반송',
      value: (stats.overview?.bounced || 0).toLocaleString(),
      icon: RotateCcw,
      color: 'orange',
      change: stats.recent24h?.bounced || 0,
    },
    {
      title: '열람',
      value: (stats.overview?.opened || 0).toLocaleString(),
      icon: Eye,
      color: 'purple',
      change: stats.recent24h?.opened || 0,
    },
    {
      title: '클릭',
      value: (stats.overview?.clicked || 0).toLocaleString(),
      icon: TrendingUp,
      color: 'indigo',
      change: stats.recent24h?.clicked || 0,
    },
    {
      title: '성공률',
      value:
        (stats.overview?.total || 0) > 0
          ? `${(((stats.overview?.sent || 0) / (stats.overview?.total || 1)) * 100).toFixed(1)}%`
          : '0%',
      icon: BarChart3,
      color: 'emerald',
      change: 0,
    },
  ]

  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-600 bg-green-50 dark:bg-green-900/20',
    red: 'text-red-600 bg-red-50 dark:bg-red-900/20',
    yellow: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
    orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20',
    purple: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
    indigo: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-900/20',
    emerald: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20',
  }

  return (
    <div className="space-y-6">
      {/* 통계 카드 */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {overviewCards.map(card => {
          const Icon = card.icon
          const colorClass =
            colorClasses[card.color as keyof typeof colorClasses]

          return (
            <div
              key={card.title}
              className="rounded-lg bg-white p-6 shadow dark:bg-gray-800"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {card.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {card.value}
                  </p>
                  {(card.change || 0) > 0 && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      24시간: +{(card.change || 0).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className={`rounded-full p-3 ${colorClass}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 템플릿별 통계 */}
      {stats.templateStats && stats.templateStats.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
            템플릿별 사용 통계
          </h3>
          <div className="space-y-3">
            {stats.templateStats.map((stat, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {stat.templateType || '알 수 없음'}
                </span>
                <div className="flex items-center space-x-3">
                  <div className="h-2 w-32 flex-1 rounded-full bg-gray-200 dark:bg-gray-700">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{
                        width: `${((stat.count || 0) / Math.max(...stats.templateStats.map(s => s.count || 0))) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <span className="min-w-[3rem] text-right text-sm text-gray-600 dark:text-gray-400">
                    {(stat.count || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// 이메일 로그 탭
